import React from "react";
import styled from "styled-components";
import { useAppSelector } from "../../store/hooks";
import { useGetMyAotdStatsQuery } from "../../services/rtkApi/artOfTheDayApi";

// Hand-made look, sibling to the yellow account box above: hard 2px black
// border, hard offset shadow (no blur), pastel fill, monospace numbers.
const Box = styled.section`
  font-family: "Courier New", Courier, monospace;
  border: var(--hm-border, 2px solid #000);
  padding: 10px;
  box-shadow: var(--hm-shadow, 5px 5px 0 0 rgba(0, 0, 0, 0.7));
  background-color: #bfe6ffd0;
  margin: 30px;
  margin-top: 12px;
  max-width: 900px;
  min-width: 94%;
`;

const Heading = styled.h3`
  font-family: inherit;
  font-size: 1.1rem;
  font-weight: bold;
  margin: 0 0 8px 5px;
  color: #111;
  letter-spacing: 0.5px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;

  @media (max-width: 520px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Cell = styled.div`
  padding: 5px;
  margin: 5px;
  border: solid 1px black;
  box-shadow: inset 2px 2px rgba(0, 0, 0, 0.483);
  text-align: center;
`;

const Value = styled.div`
  font-size: 1.6rem;
  font-weight: bold;
  line-height: 1.2;
  color: #111;
`;

const Label = styled.div`
  font-size: 0.75rem;
  color: #1f3140;
`;

const Sub = styled.div`
  font-size: 0.7rem;
  margin-top: 2px;
  color: #2a4457;
`;

const Note = styled.p`
  margin: 6px 5px 0 5px;
  font-size: 0.7rem;
  color: #2a4457;
`;

const EmptyLine = styled.p`
  margin: 5px;
  padding: 5px;
  font-size: 0.9rem;
  color: #1f3140;
`;

/**
 * Race offsets arrive as milliseconds from the day's unlock, which nobody
 * reads as a duration. Sub-second taps keep two decimals (0.23s), because
 * that is the range the race actually lives in; a minute or more folds into
 * "1m 12s".
 */
function formatOffset(ms: number): string {
  if (ms < 1000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const totalSeconds = Math.round(ms / 1000);
  return `${Math.floor(totalSeconds / 60)}m ${totalSeconds % 60}s`;
}

const formatRank = (rank: number, of: number) => `#${rank} of ${of}`;

/**
 * "aotd stats" — the member's own RACE numbers. How often they win when
 * somebody else shows up, how fast they tap, and which shift is theirs.
 *
 * Everything here counts CONTESTED races only (a day nobody else raced is
 * neither a win nor a loss), except the tap times, which include solo claims
 * because they measure reaction rather than competition. Ranks stay hidden
 * until the member has enough races to be worth ranking.
 *
 * Stays hidden until the session is restored, and while the request is in
 * flight or failed, so the profile never flashes a broken or all-zero box.
 */
const AotdStatsBox: React.FC = () => {
  const bootstrapStatus = useAppSelector((state) => state.auth.bootstrapStatus);
  const token = useAppSelector((state) => state.auth.token);
  const ready = bootstrapStatus === "ready" && !!token;

  const { data } = useGetMyAotdStatsQuery(undefined, { skip: !ready });

  if (!ready || !data) return null;

  const raced = data.racesJoined > 0;
  const hasHistory = raced || data.timedRaces > 0;
  const windowSeconds = Math.round(data.entryWindowMs / 1000);

  return (
    <Box>
      <Heading>aotd stats</Heading>
      {hasHistory ? (
        <>
          <Grid>
            {raced && (
              <Cell>
                <Value>{Math.round(data.successRate * 100)}%</Value>
                <Label>success rate</Label>
                {data.successRateRank !== null && (
                  <Sub>
                    {formatRank(data.successRateRank, data.successRateRankOf)}
                  </Sub>
                )}
              </Cell>
            )}
            {data.avgOffsetMs !== null && (
              <Cell>
                <Value>{formatOffset(data.avgOffsetMs)}</Value>
                <Label>avg tap</Label>
                {data.avgSpeedRank !== null && (
                  <Sub>{formatRank(data.avgSpeedRank, data.avgSpeedRankOf)}</Sub>
                )}
              </Cell>
            )}
            {data.fastestOffsetMs !== null && (
              <Cell>
                <Value>{formatOffset(data.fastestOffsetMs)}</Value>
                <Label>fastest tap</Label>
              </Cell>
            )}
            {raced && (
              <Cell>
                <Value>{data.racesJoined}</Value>
                <Label>{data.racesJoined === 1 ? "race joined" : "races joined"}</Label>
              </Cell>
            )}
            {raced && (
              <Cell>
                <Value>{data.wins}</Value>
                <Label>{data.wins === 1 ? "win" : "wins"}</Label>
              </Cell>
            )}
            {data.favoriteShift && (
              <Cell>
                <Value>{data.favoriteShift.timezone}</Value>
                <Label>favorite shift</Label>
                <Sub>
                  {data.favoriteShift.races}{" "}
                  {data.favoriteShift.races === 1 ? "race" : "races"},{" "}
                  {data.favoriteShift.wins} won
                </Sub>
              </Cell>
            )}
          </Grid>
          {raced && !data.qualified && (
            <Note>need {data.minRacesForRank} contested races for a rank</Note>
          )}
          {data.avgOffsetMs !== null && (
            <Note>
              tap times count the first {windowSeconds}s after unlock
            </Note>
          )}
        </>
      ) : (
        <EmptyLine>no races yet. tap in when the day unlocks.</EmptyLine>
      )}
    </Box>
  );
};

export default AotdStatsBox;
